import { supabase } from '../supabase'
import type { RoomRow } from '../database.types'

/**
 * Ensure there's a Supabase session on this device. TV displays and
 * phone joiners both sign in anonymously — no email/password needed.
 * Returns the session user's id so the caller can confirm.
 *
 * `displayName` seeds the profile only for an ANONYMOUS identity, where
 * the profile is that identity and nothing else claims it. A signed-in
 * account is never renamed: the name typed on /join belongs to that
 * ROOM, not to the person's account, and this used to overwrite it.
 * Rooms carry their own per-seat name, which is where a party name
 * should live.
 *
 * The one exception is an anonymous profile still sitting on the literal
 * placeholder, which is how an Apple sign-in with no name could get
 * stuck showing "Player" in every room.
 */
const PLACEHOLDER_NAME = /^(player|guest|guest [0-9a-f]{1,8}|tv|tv \(.*\))$/i
export async function ensureAnonSession(displayName?: string): Promise<string> {
  const { data: existing } = await supabase.auth.getSession()
  if (existing.session?.user) {
    const user = existing.session.user
    // Real accounts are never renamed by joining a party.
    const isAnon = (user as { is_anonymous?: boolean }).is_anonymous === true
    if (isAnon && displayName?.trim()) {
      const { data: me } = await supabase
        .from('profiles')
        .select('display_name')
        .eq('id', user.id)
        .maybeSingle()
      const current = (me as { display_name?: string } | null)?.display_name?.trim() ?? ''
      if (!current || PLACEHOLDER_NAME.test(current)) {
        await setDisplayName(displayName.trim())
      }
    }
    return user.id
  }

  const { data, error } = await supabase.auth.signInAnonymously({
    options: displayName ? { data: { display_name: displayName } } : undefined,
  })
  if (error) throw error
  const userId = data.user?.id
  if (!userId) throw new Error('Anonymous sign-in returned no user')

  // The handle_new_user trigger creates a profile row from
  // raw_user_meta_data. Re-apply via setDisplayName so the typed name
  // wins even if metadata wasn't honored.
  if (displayName) await setDisplayName(displayName)
  return userId
}

/** Update the caller's profile.display_name. */
export async function setDisplayName(displayName: string): Promise<void> {
  const { data: { user } } = await supabase.auth.getUser()
  if (!user) throw new Error('Not signed in')

  // Plain UPDATE, not UPSERT. The handle_new_user trigger always
  // creates the profile row at sign-in time, so the row is guaranteed
  // to exist by the time we get here. We previously used `.upsert(...)`,
  // but Postgres checks NOT NULL on the proposed-insert tuple BEFORE
  // resolving ON CONFLICT, so the missing `username` (auto-set by the
  // trigger) always tripped the not-null check and the call failed
  // with "null value in column 'username' of relation 'profiles'…"
  // even when the row was already there.
  const { error } = await supabase
    .from('profiles')
    .update({ display_name: displayName })
    .eq('id', user.id)
  if (error) throw error
}

/** Look up a room by code WITHOUT joining — used for the phone's "is this a real code?" check. */
export async function lookupRoomByCode(code: string): Promise<RoomRow | null> {
  const { data, error } = await supabase
    .from('rooms')
    .select('*')
    .eq('code', code.toUpperCase())
    .maybeSingle()
  if (error) throw error
  return (data as RoomRow) ?? null
}

/** Player submits one course (TV mode writes one course at a time). */
export async function submitSingleCourse(
  roomId: string,
  course: 1 | 2 | 3,
  text: string,
  ids: string[],
): Promise<void> {
  const { error } = await supabase.rpc('submit_single_course', {
    p_room_id: roomId,
    p_course: course,
    p_text: text,
    p_ids: ids,
  })
  if (error) throw error
}

/**
 * Host to print on join/TV links.
 *
 * Native builds and the real domains get the canonical public host, so a
 * QR scanned off a phone always resolves (and because WKWebView has no
 * SPA fallback for a relative path). Anywhere else — localhost, a
 * preview deploy — use the origin you are actually on, or every link
 * during testing points at production, which is a different build.
 */
export function publicOrigin(): string {
  const o = typeof window !== 'undefined' ? window.location.origin : ''
  return /^https?:\/\/(localhost|127\.0\.0\.1|\[::1\])(:\d+)?$/.test(o)
    ? o
    : 'https://games.jordandraper.com'
}
